import { forwardRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { reapplyHistoryEntry } from '@/store/actions.ts';
import { getModelFromCatalog, getModelCost, clampDuration } from '@/ai/tokenCosts.ts';
import {
  buildPromptContext, buildMetadataSuffix, buildVideoPrompt, buildTransitionVideoPrompt,
  DEFAULT_BASE_PROMPT, DEFAULT_VIDEO_PROMPT, DEFAULT_TRANSITION_VIDEO_PROMPT,
} from '@/ai/promptBuilder.ts';
import { GenerationHistoryPanel } from './GenerationHistoryPanel.tsx';
import { showToast } from './Toast.tsx';
import type { TimelineShot } from '@/types/timeline.ts';
import type { CatalogModel } from '@/types/ai.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import styles from './Viewer.module.css';

// ── Types ──────────────────────────────────────────────────────────

export type ConfirmMode = 'enhance' | 'video' | 'transition';

export interface ConfirmOpts {
  customPrompt?: string;
  generateAudio?: boolean;
  audioHint?: string;
}

// ── Unified AI Confirm Popover ──────────────────────────────────────

interface AiConfirmPopoverProps {
  mode: ConfirmMode;
  shot: TimelineShot;
  fromShot?: TimelineShot;
  toShot?: TimelineShot;
  catalog: CatalogModel[];
  onConfirm: (opts: ConfirmOpts) => void;
  onCancel: () => void;
}

const MODE_CONFIG = {
  enhance: {
    titleKey: 'enhanceShotTitle',
    actionLabelKey: 'enhanceActionLabel',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
      </svg>
    ),
  },
  video: {
    titleKey: 'generateVideoTitle',
    actionLabelKey: 'generateVideoActionLabel',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  transition: {
    titleKey: 'generateTransitionTitle',
    actionLabelKey: 'generateVideoActionLabel',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" /><polyline points="12 5 19 12 12 19" />
      </svg>
    ),
  },
} as const;

export const AiConfirmPopover = forwardRef<HTMLDivElement, AiConfirmPopoverProps>(
  function AiConfirmPopover({ mode, shot, fromShot, toShot, catalog, onConfirm, onCancel }, ref) {
    const { t } = useTranslation('viewer');
    const aiSelectedImageModel = useAiStore(s => s.aiSelectedImageModel);
    const aiSelectedVideoModel = useAiStore(s => s.aiSelectedVideoModel);
    const setSelectedModel = useAiStore(s => s.setSelectedModel);
    const aiUseMetadata = useAiStore(s => s.aiUseMetadata);
    const retimeToMatchVideo = useAiStore(s => s.retimeToMatchVideo);
    const [modelPickerOpen, setModelPickerOpen] = useState(false);

    // ── History picker ──────────────────────────────────────────────
    const [historyPickerOpen, setHistoryPickerOpen] = useState(false);
    const historyFilter = mode === 'enhance' ? 'image' as const : 'video' as const;

    const handleHistoryPick = useCallback((entry: GenerationHistoryEntry) => {
      if (mode === 'enhance') {
        reapplyHistoryEntry(entry.id, shot.id);
      } else if (mode === 'transition' && fromShot && toShot) {
        if (entry.assetType === 'transition_video') {
          const ps = useProjectStore.getState();
          const existing = ps.getTransition(fromShot.id, toShot.id);
          if (!existing || existing.type !== 'continuous') {
            ps.setTransition(fromShot.id, toShot.id, 'continuous');
          }
          ps.attachTransitionVideo(fromShot.id, toShot.id, entry.objectUrl, entry.mimeType, entry.id);
        } else if (entry.assetType === 'video') {
          reapplyHistoryEntry(entry.id, fromShot.id);
        }
      } else if (mode === 'video') {
        if (entry.assetType === 'transition_video') {
          // Find next shot for continuous transition
          const ps = useProjectStore.getState();
          const project = ps.project;
          if (project) {
            let nextShotId: number | null = null;
            for (const scene of project.scenes) {
              const idx = scene.shots.findIndex(s => s.id === shot.id);
              if (idx >= 0 && idx < scene.shots.length - 1) {
                nextShotId = scene.shots[idx + 1].id;
                break;
              }
            }
            if (nextShotId) {
              const existing = ps.getTransition(shot.id, nextShotId);
              if (!existing || existing.type !== 'continuous') {
                ps.setTransition(shot.id, nextShotId, 'continuous');
              }
              ps.attachTransitionVideo(shot.id, nextShotId, entry.objectUrl, entry.mimeType, entry.id);
            } else {
              showToast(t('noNextShotForTransition'), 'error');
              return;
            }
          }
        } else {
          reapplyHistoryEntry(entry.id, shot.id);
        }
      }
      setHistoryPickerOpen(false);
      onCancel();
    }, [mode, shot, fromShot, toShot, onCancel, t]);

    const modelCategory = mode === 'enhance' ? 'image' as const : 'video' as const;
    const modelId = mode === 'enhance' ? aiSelectedImageModel : aiSelectedVideoModel;
    const modelInfo = getModelFromCatalog(modelId, catalog);
    const modelName = modelInfo?.name ?? modelId;
    // Video is per-second: price from this shot's (clamped) duration; transitions run from the outgoing shot.
    const costDuration = mode === 'enhance' ? undefined
      : mode === 'transition' ? (fromShot?.duration ?? shot.duration)
      : shot.duration;
    const cost = getModelCost(modelId, catalog, costDuration);
    const resolution = modelInfo?.resolution;
    const durLimits = modelInfo?.durationLimits;
    const clampedDur = (mode === 'video') ? clampDuration(shot.duration, modelId, catalog) : null;
    const durRange = durLimits ? `${durLimits.min}\u2013${durLimits.max}s` : null;
    const config = MODE_CONFIG[mode];

    const availableModels = catalog
      .filter(m => m.category === modelCategory && m.id !== 'flux2-flash' && m.id !== 'kling-v3')
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const handleModelSelect = useCallback((newModelId: string) => {
      setSelectedModel(modelCategory, newModelId);
      setModelPickerOpen(false);
    }, [modelCategory, setSelectedModel]);

    // Build default prompt for current mode
    const buildDefaultPrompt = useCallback(() => {
      if (mode === 'enhance') {
        const base = DEFAULT_BASE_PROMPT;
        if (!aiUseMetadata) return base;
        const ps = useProjectStore.getState();
        const scene = ps.project?.scenes.find(s => s.shots.some(sh => sh.id === shot.id));
        if (!scene) return base;
        const ctx = buildPromptContext(shot, { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay });
        const suffix = buildMetadataSuffix(ctx);
        return `${base} ${suffix}.`;
      }
      if (mode === 'video') {
        if (!aiUseMetadata) return DEFAULT_VIDEO_PROMPT;
        const ps = useProjectStore.getState();
        const scene = ps.project?.scenes.find(s => s.shots.some(sh => sh.id === shot.id));
        if (!scene) return DEFAULT_VIDEO_PROMPT;
        const ctx = buildPromptContext(shot, { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay });
        return buildVideoPrompt(ctx, true);
      }
      // transition
      if (!aiUseMetadata || !fromShot || !toShot) return DEFAULT_TRANSITION_VIDEO_PROMPT;
      const ps = useProjectStore.getState();
      const scene = ps.project?.scenes.find(s => s.shots.some(sh => sh.id === fromShot.id));
      if (!scene) return DEFAULT_TRANSITION_VIDEO_PROMPT;
      return buildTransitionVideoPrompt(fromShot, toShot, { name: scene.name, locationType: scene.locationType, timeOfDay: scene.timeOfDay }, true);
    }, [mode, shot, fromShot, toShot, aiUseMetadata]);

    const [promptOpen, setPromptOpen] = useState(false);
    const [editablePrompt, setEditablePrompt] = useState(() => buildDefaultPrompt());
    const [generateAudio, setGenerateAudio] = useState(false);
    const [audioHint, setAudioHint] = useState('');

    // Rebuild prompt when metadata toggle changes
    useEffect(() => {
      setEditablePrompt(buildDefaultPrompt());
    }, [aiUseMetadata, buildDefaultPrompt]);

    const handleReset = useCallback(() => {
      setEditablePrompt(buildDefaultPrompt());
    }, [buildDefaultPrompt]);

    const handleToggleMetadata = useCallback(() => {
      useAiStore.getState().toggleAiUseMetadata();
    }, []);

    const handleToggleRetime = useCallback(() => {
      useAiStore.getState().toggleRetimeToMatchVideo();
    }, []);

    const handleSubmit = useCallback(() => {
      const defaultPrompt = buildDefaultPrompt();
      const customPrompt = editablePrompt.trim() !== defaultPrompt.trim() ? editablePrompt.trim() : undefined;
      onConfirm({
        customPrompt,
        generateAudio: mode === 'video' ? generateAudio : undefined,
        audioHint: mode === 'video' && generateAudio && audioHint.trim() ? audioHint.trim() : undefined,
      });
    }, [onConfirm, mode, editablePrompt, generateAudio, audioHint, buildDefaultPrompt]);

    const showResolution = mode !== 'enhance' && resolution;
    const showDuration = mode === 'video' && clampedDur != null;
    const showAudioToggle = mode === 'video';
    const showRetimeToggle = mode === 'video';

    return (
      <div ref={ref} className={styles.aiConfirmPopover}>
        {/* Header */}
        <div className={styles.aiConfirmHeader}>
          <span className={styles.aiConfirmHeaderIcon}>{config.icon}</span>
          <span className={styles.aiConfirmTitle}>{t(config.titleKey)}</span>
          <button
            className={styles.aiConfirmHistoryBtn}
            onClick={() => setHistoryPickerOpen(true)}
            title={t('loadFromHistoryTooltip')}
          >
            <svg width="14" height="14" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v5h5" />
              <path d="M3 8a9 9 0 1 1 1.83 5.5" />
              <polyline points="12 7 12 12 15.5 14" />
            </svg>
          </button>
        </div>

        {/* Info row — model name is clickable to swap */}
        <div className={styles.aiConfirmInfoRow} style={{ position: 'relative' }}>
          <button
            className={styles.aiConfirmModelBtn}
            onClick={() => setModelPickerOpen(prev => !prev)}
            title={t('changeModelTooltip')}
          >
            {modelName}
            <span className={styles.aiConfirmModelArrow}>{modelPickerOpen ? '\u25B4' : '\u25BE'}</span>
          </button>
          {showResolution && (
            <><span className={styles.aiConfirmInfoSep}>&middot;</span><span>{resolution}</span></>
          )}
          {showDuration && (
            <><span className={styles.aiConfirmInfoSep}>&middot;</span><span>{t('durationRange', { duration: clampedDur, range: durRange })}</span></>
          )}
          <span className={styles.aiConfirmInfoSep}>&middot;</span>
          <span>{t('creditsCost', { cost })}</span>
          {/* Inline model picker dropdown */}
          {modelPickerOpen && availableModels.length > 1 && (
            <div className={styles.aiConfirmModelDropdown}>
              {availableModels.map(m => (
                <button
                  key={m.id}
                  className={`${styles.aiConfirmModelOption} ${m.id === modelId ? styles.aiConfirmModelOptionActive : ''}`}
                  onClick={() => handleModelSelect(m.id)}
                >
                  <span className={styles.aiConfirmModelOptName}>{m.name}</span>
                  <span className={styles.aiConfirmModelOptCost}>{m.cost} cr{m.resolution ? ` \u00B7 ${m.resolution}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Trust messaging */}
        <div className={styles.aiConfirmTrust}>{t('creditsDeductedOnlyOnSuccess')}</div>

        {/* Video-only toggles */}
        {showAudioToggle && (
          <>
            <div className={styles.aiConfirmCheckRow}>
              <input
                type="checkbox"
                id="acpAudio"
                checked={generateAudio}
                onChange={(e) => setGenerateAudio(e.target.checked)}
              />
              <label htmlFor="acpAudio">{t('generateAudioCheckbox')}</label>
            </div>
            {generateAudio && (
              <input
                className={styles.aiConfirmTextInput}
                type="text"
                placeholder={t('audioDirectionPlaceholder')}
                value={audioHint}
                onChange={(e) => setAudioHint(e.target.value)}
              />
            )}
          </>
        )}
        {showRetimeToggle && (
          <div className={styles.aiConfirmCheckRow}>
            <input
              type="checkbox"
              id="acpRetime"
              checked={retimeToMatchVideo}
              onChange={handleToggleRetime}
            />
            <label htmlFor="acpRetime">{t('retimeShotCheckbox')}</label>
          </div>
        )}

        {/* Divider before prompt section */}
        {(showAudioToggle || showRetimeToggle) && <div className={styles.aiConfirmDivider} />}

        {/* Prompt disclosure */}
        <button className={styles.aiConfirmDisclosure} onClick={() => setPromptOpen(prev => !prev)}>
          <span className={`${styles.aiConfirmDisclosureArrow} ${promptOpen ? styles.aiConfirmDisclosureArrowOpen : ''}`}>
            &#x25B8;
          </span>
          {t('editPromptDisclosure')}
        </button>

        <div className={`${styles.aiConfirmDisclosureContent} ${promptOpen ? styles.aiConfirmDisclosureContentOpen : ''}`}>
          <div className={styles.aiConfirmDisclosureInner}>
            <textarea
              className={styles.aiConfirmTextarea}
              rows={4}
              value={editablePrompt}
              onChange={(e) => setEditablePrompt(e.target.value)}
            />
            <div className={styles.aiConfirmResetRow}>
              <button className={styles.aiConfirmResetBtn} onClick={handleReset}>{t('resetPromptButton')}</button>
              <div className={styles.aiConfirmMetaCheck}>
                <input
                  type="checkbox"
                  id="acpMeta"
                  checked={aiUseMetadata}
                  onChange={handleToggleMetadata}
                />
                <label htmlFor="acpMeta">{t('useMetadataCheckbox')}</label>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className={styles.aiConfirmButtons}>
          <button className={styles.aiConfirmBtnCancel} onClick={onCancel}>{t('cancelButton')}</button>
          <button className={styles.aiConfirmBtnGo} onClick={handleSubmit}>{t(config.actionLabelKey)}</button>
        </div>

        {historyPickerOpen && (
          <GenerationHistoryPanel
            onPick={handleHistoryPick}
            onDismiss={() => setHistoryPickerOpen(false)}
            initialFilter={historyFilter}
          />
        )}
      </div>
    );
  }
);
